import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useToast,
} from "@chakra-ui/react";
import { partial } from "filesize";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDropzone } from "react-dropzone";
import { useParams } from "react-router-dom";
import { getLocalUserProfile } from "../../../services/auth.service";
import program_moduleService from "../../../services/program_service/program_module.service";
import trainercoursesService from "../../../services/trainer/trainercourses.service";

export const UploadProgramResultModal: React.FC<any> = ({
  isOpen,
  onClosed,
  session,
  term,
  ID,
  course_id,
  reload,
}: {
  isOpen: any;
  onClosed: any;
  session: any;
  term: any;
  ID: any;
  course_id: string;
  reload: any;
}) => {
  const toast = useToast();
  const params = useParams();
  const cancelRef = useRef().current;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [batchFile, setbatchFile] = useState<FormData | null>(null);
  const [filename, setFilename] = useState("");
  const [uploadFileSize, setUploadFileSize] = useState("");
  const [preview, setPreview] = useState<string | undefined>();
  const [userId, setUserId] = useState<any>();
  const [courseId, setCourseId] = useState<any>("");
  const [programModules, setProgramModules] = useState<any>([]);
  const [formValues, setFormValues] = useState({
    passScore: "",
    meritScore: "",
    resultAt: "",
    moduleId: "",
    maxScore: "",
  });

  const [formErrors, setFormErrors] = useState({
    passScore: "",
    meritScore: "",
    resultAt: "",
    moduleId: "",
    maxScore: "",
    templateFile: "",
  });

  useEffect(() => {
    const user = getLocalUserProfile();
    if (user) setUserId(user.id);
  }, []);

  useEffect(() => {
    try {
      program_moduleService.getProgramModuleById(course_id).then((modules) => {
        setProgramModules(modules.data);
      });
    } catch (e) {}
  }, [course_id]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const fileName = e.target.files?.item(0)?.name as string;
      const fileType = e.target.files?.item(0)?.type;
      // Check if the file extension is .xlsx
      if (
        fileType !==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        toast({
          title: "File Upload",
          description: "Only Excel (.xlsx) files are supported",
          status: "error",
        });
        return;
      }
      const file = e.target.files?.item(0) as File;
      setFilename(fileName);
      setTemplateFile(file);
      const fileSize = partial({ base: 2, standard: "jedec" });
      setUploadFileSize(fileSize(file.size));
    } else {
      setFilename("");
      setTemplateFile(null);
    }
  };

  const handleChange = (e: { target: { name: any; value: any } }) => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value,
    });
  };

  const handleResultUpload = async (e: any) => {
    e.preventDefault();
    const { isValid } = validateForm();

    if (isValid && templateFile) {
      const formData = new FormData();
      setLoading(true);
      setFormValues({
        ...formValues,
        resultAt: "module",
      });
      formData.append("template", templateFile);
      formData.append("program_session_id", session.toString());
      formData.append("program_term_id", term.toString());
      formData.append("trainer_id", userId?.toString());
      formData.append("pass_score", formValues.passScore);
      formData.append("merit_score", formValues.meritScore);
      formData.append("result_at", "module"); // considering only modules for now
      formData.append("module_id", formValues.moduleId.toString());
      formData.append("max_score", formValues.maxScore);

      try {
        const uploadResultsRes =
          await trainercoursesService.uploadProgramResults(formData, course_id);

        toast({
          title: "Result Upload",
          description: uploadResultsRes.message,
          status: "success",
          duration: 2000,
        });
        handleCloseModal();
        reload();
      } catch (error: any) {
        toast({
          title: "Result Upload",
          description: `${error.response.data.message}`,
          status: "error",
          duration: 2000,
        });
      } finally {
        setLoading(false);
      }
    } else {
      toast({
        title: "Result Upload",
        description: "Please fill all the fields",
        status: "info",
        duration: 2000,
      });
    }
  };

  const validateForm = () => {
    let isValid = true;
    const errors: any = {};

    // Validate Pass Score
    if (!formValues.passScore.trim()) {
      isValid = false;
      errors.passScore = "Pass score is required";
    } else if (
      isNaN(Number(formValues.passScore)) ||
      Number(formValues.passScore) < 0 ||
      Number(formValues.passScore) > 100
    ) {
      isValid = false;
      errors.passScore = "Pass score must be a number between 0 and 100";
    }

    // Validate Merit Score
    if (!formValues.meritScore.trim()) {
      isValid = false;
      errors.meritScore = "Merit score is required";
    } else if (
      isNaN(Number(formValues.meritScore)) ||
      Number(formValues.meritScore) < 0 ||
      Number(formValues.meritScore) > 100
    ) {
      isValid = false;
      errors.meritScore = "Merit score must be a number between 0 and 100";
    }

    // Validate Max Score
    if (!formValues.maxScore.trim()) {
      isValid = false;
      errors.maxScore = "Max score is required";
    } else if (
      isNaN(Number(formValues.maxScore)) ||
      Number(formValues.maxScore) < 0 ||
      Number(formValues.maxScore) > 100
    ) {
      isValid = false;
      errors.maxScore = "Max score must be a number between 0 and 100";
    }

    // Validate Module ID
    if (!formValues.moduleId.trim()) {
      isValid = false;
      errors.moduleId = "Please select a module";
    }

    // Validate Result Template File
    if (!templateFile) {
      isValid = false;
      errors.templateFile = "A result template file is required";
    }

    setFormErrors(errors);

    return { isValid, errors };
  };

  const handleCloseModal = () => {
    setFormValues({
      passScore: "",
      meritScore: "",
      resultAt: "",
      moduleId: "",
      maxScore: "",
    });
    setFormErrors({
      passScore: "",
      meritScore: "",
      resultAt: "",
      moduleId: "",
      maxScore: "",
      templateFile: "",
    });

    setTemplateFile(null);
    setFilename("");
    setUploadFileSize("");
    setPreview(undefined);
    setbatchFile(null);

    setLoading(false);

    onClosed();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCloseModal}
        scrollBehavior="inside"
        closeOnOverlayClick={true}
        size="md"
      >
        <ModalOverlay />
        <ModalContent borderRadius={5} px={4}>
          <ModalHeader
            fontSize="21px"
            fontWeight="700"
            pb={1}
            textTransform="none"
            pl={3}
            color="#2B2B2B"
          >
            Upload Result
          </ModalHeader>
          <ModalCloseButton size="sm" />
          <ModalBody pb={6}>
            <Box mt={2}>
              <form onSubmit={(e: any) => handleResultUpload(e)}>
                <FormControl mt={4} hidden>
                  <FormLabel fontWeight="700" color="#757575" fontSize="14px">
                    Result for
                  </FormLabel>
                  <Select
                    variant="outline"
                    bg="inputText.500"
                    size="md"
                    placeholder="Select an option"
                    value={formValues.resultAt}
                    name="resultAt"
                    onChange={(e) => {
                      handleChange(e);
                      const selectedValue = e.target.value;
                      if (selectedValue === "course") {
                        setCourseId(params.course_id);
                      }
                    }}
                    fontSize="14px"
                    color="brand.subtitle"
                  >
                    {["course", "module"].map((each: string, index: number) => (
                      <option key={index} value={each}>
                        {" "}
                        {each}{" "}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  mt={4}
                  isInvalid={formErrors.moduleId ? true : false}
                >
                  <FormLabel fontWeight="700" color="#757575" fontSize="14px">
                    Select Module
                  </FormLabel>
                  <Select
                    variant="outline"
                    bg="inputText.500"
                    size="md"
                    placeholder="Select an option"
                    value={formValues.moduleId}
                    name="moduleId"
                    fontSize="14px"
                    color="brand.subtitle"
                    onChange={(e: { target: { name: any; value: any } }) =>
                      handleChange(e)
                    }
                  >
                    {programModules?.map((each: any, index: number) => (
                      <option key={index} value={each.module.id}>
                        {" "}
                        {each.module.code}{" "}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{formErrors.moduleId}</FormErrorMessage>
                </FormControl>

                <FormControl
                  mt={4}
                  isInvalid={formErrors.passScore ? true : false}
                >
                  <FormLabel fontWeight="700" color="#757575" fontSize="14px">
                    Pass Mark (%)
                  </FormLabel>
                  <Input
                    type="number"
                    id="passScore"
                    name="passScore"
                    onChange={(e: { target: { name: any; value: any } }) =>
                      handleChange(e)
                    }
                    fontSize={13}
                  />
                  <FormErrorMessage>{formErrors.passScore}</FormErrorMessage>
                </FormControl>
                <FormControl
                  mt={4}
                  isInvalid={formErrors.meritScore ? true : false}
                >
                  <FormLabel fontWeight="700" color="#757575" fontSize="14px">
                    Merit Score (%)
                  </FormLabel>
                  <Input
                    type="number"
                    id="meritScore"
                    name="meritScore"
                    onChange={(e: { target: { name: any; value: any } }) =>
                      handleChange(e)
                    }
                    fontSize={13}
                  />
                  <FormErrorMessage>{formErrors.meritScore}</FormErrorMessage>
                </FormControl>
                <FormControl
                  mt={4}
                  isInvalid={formErrors.maxScore ? true : false}
                >
                  <FormLabel fontWeight="700" color="#757575" fontSize="14px">
                    Max Score (%)
                  </FormLabel>
                  <Input
                    type="number"
                    id="maxScore"
                    name="maxScore"
                    onChange={(e: { target: { name: any; value: any } }) =>
                      handleChange(e)
                    }
                    fontSize={13}
                  />
                  <FormErrorMessage>{formErrors.maxScore}</FormErrorMessage>
                </FormControl>
                <FormControl
                  mr={10}
                  mt={4}
                  isInvalid={formErrors.templateFile ? true : false}
                >
                  <FormLabel fontWeight="600" color="#757575" fontSize={"14px"}>
                    Result Template
                  </FormLabel>
                  <Text fontWeight="400" color="#959595" fontSize="14px">
                    Upload an excel template of the result. (Not larger than
                    2MB)
                  </Text>

                  <input
                    accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    type="file"
                    name="templateFile"
                    ref={inputRef}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleFileChange(e)
                    }
                    style={{ display: "none" }}
                  />
                  <Input
                    cursor="pointer"
                    placeholder="No file selected"
                    defaultValue={filename}
                    mt="10px"
                    borderColor="#eeeeee"
                    fontSize={14}
                    bg="#fafafa"
                    onClick={() => inputRef?.current?.click()}
                    border="2px dashed"
                    borderRadius={2}
                  />
                  <FormErrorMessage>{formErrors.templateFile}</FormErrorMessage>
                </FormControl>
                <Flex mt={12} justifyContent="end">
                  <HStack>
                    <Button
                      size="sm"
                      px={6}
                      fontSize={12}
                      onClick={() => handleCloseModal()}
                    >
                      Cancel
                    </Button>
                    <Button
                      colorScheme="red"
                      type="submit"
                      size="sm"
                      px={6}
                      fontSize={12}
                      isLoading={loading}
                      disabled={loading}
                    >
                      {" "}
                      Upload
                    </Button>
                  </HStack>
                </Flex>
              </form>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
